import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { Chart as ChartJS, Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getLoginsByProduct } from '../../../API/UtilizationReportingDashboard';
import { constructChartData, downloadDivChart, getTimePeriodLabel, utilizationReportConstants, setChartYAxes, baseBarChartOptions } from './utlizationChart';
import { useErrorBoundary } from "react-error-boundary";
import ReportInfoTooltip from "../ReportInfoTooltip";
import Button from 'react-bootstrap/Button';
import ChartDataLabels from "chartjs-plugin-datalabels";
import { createColorPaletteRegistry } from '../ColorRegistry';

TotalLoginsByProductChart.propTypes = {
    filterState: PropType.object,
    colorRegistry: PropType.object
}

function TotalLoginsByProductChart(props) {
    const {
        filterState,
        colorRegistry
    } = props;

    const { showBoundary } = useErrorBoundary();
    const [appState, setAppState] = useContext(AppContext);
    const chartRef = useRef(null);
    const [data, setData] = useState({});
    const [chartData, setChartData] = useState({});
    const [isLoading, setIsLoading] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
    const tooltipTarget = useRef(null); 
    const [maxYValue, setMaxYValue] = useState(null);
    const [yAxesStepIncrementValue, setYAxesStepIncrementValue] = useState(1);
    const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.TotalLoginsByProduct?.Enabled, appState)?.Value === 'true') || false;
    const [chartColorRegistry] = useState(() => createColorPaletteRegistry(
        JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.ColorPalette, appState)?.Value) || [],
        { storageKey: 'utilizationReportingDashboard:totalLogins:colorpalette' }
    ));

    ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);
 
    const getData = async () => {
        if(filterState == undefined || filterState?.selectedProductIds == null) {
            return;
        }

        setIsLoading(true);
        await getLoginsByProduct({
                "timeFilter": filterState.timePeriodId,
                "productIds": filterState.selectedProductIds,
                "includePriorPeriod": true
        })        
        .then((responseJson) => {
            setData(responseJson);

            const filteredDataSet = filterState.allProducts 
            ? responseJson.currentPeriod.dataSets.filter(item => item.label === utilizationReportConstants.allProducts) 
            : responseJson.currentPeriod.dataSets.filter(item => item.label !== utilizationReportConstants.allProducts);

            setChartData({labels: responseJson.currentPeriod.labels, datasets: constructChartData(filteredDataSet, chartColorRegistry, appState)
            });    

            setChartYAxes(filteredDataSet, setYAxesStepIncrementValue, setMaxYValue);
        })
        .catch((err) => {
            showBoundary(err);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    const toggleChartTab = (activeTab, selectedData) => {
        const filteredDataSet = filterState.allProducts 
        ? selectedData.dataSets.filter(item => item.label === utilizationReportConstants.allProducts) 
        : selectedData.dataSets.filter(item => item.label !== utilizationReportConstants.allProducts);
        
        setChartData({labels: selectedData.labels, datasets: constructChartData(filteredDataSet, chartColorRegistry, appState)});  
        setActiveButton(activeTab); 
        setChartYAxes(filteredDataSet, setYAxesStepIncrementValue, setMaxYValue);
    }
    
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: 0
        },
        plugins: {
            legend: baseBarChartOptions.legend,
            datalabels: baseBarChartOptions.datalabels,
            title: {
                ...baseBarChartOptions.title,
                text: getTimePeriodLabel(filterState.timePeriodId, activeButton, true),
            },
            tooltip: baseBarChartOptions.tooltip,
        },
        scales: {
            x: baseBarChartOptions.scales.x,
            y: {
                ...baseBarChartOptions.scales.y,
                max: maxYValue,
                ticks: {
                    ...baseBarChartOptions.scales.y.ticks,
                    stepSize: yAxesStepIncrementValue
                }
            }
        },
        resizeDelay:  200,
    };


    useEffect(() => {
        if (chartEnabled && appState?.config && !isLoading) {                
            getData();      
            setActiveButton(utilizationReportConstants.current);     
        }
    }, [filterState.timePeriodId, filterState.selectedProductIds]);


    let content;
    if (isLoading === null || isLoading) {
        content =  (<div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>);
    }
    else {
        content = (
            <>
                <div id="totalLoginsByProduct-ChartsArea" className='chart-canvas-wrapper'>
                    <Bar className={`chart ${isCollapsed ? 'collapsed' : ''}`} ref={chartRef} options={chartOptions} data={chartData} />              
                </div>  
                <div className='reportButtons-container'>   
                    <Button 
                        className='reportTextButton' 
                        onClick={() => downloadDivChart('totalLoginsByProduct-ChartsArea', getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.TotalLoginsByProduct?.Title, appState)?.Value || null)}
                        >
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null} <i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
                    </Button>
                </div>               
                <div className='reportTabButton-container'>
                    <Button 
                        className={`tab-button ${activeButton === utilizationReportConstants.current ? "active" : "inactive" }`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.current, data.currentPeriod);}}
                        >
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} ({filterState.timePeriodTitle})
                    </Button>
                    <Button 
                        className={`tab-button ${activeButton === utilizationReportConstants.prior ? "active" : "inactive" }`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.prior, data.priorPeriod);}}
                        >
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
        {chartEnabled &&
        <div className='chart-panel'>
            <div className='chart-header'>
                <Button className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`} onClick={()=> {setIsCollapsed(!isCollapsed)}}>
                    {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.TotalLoginsByProduct?.Title, appState)?.Value || null}
                    <ReportInfoTooltip ref={tooltipTarget} text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.TotalLoginsByProduct?.ToolTip, appState )?.Value} />  
                </Button>   
            </div>
            <div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>                
                {content}                
            </div>
        </div>
        }
        </>
    );
}

export default TotalLoginsByProductChart;